import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import Box from '../../../elements/Box';
import Text from '../../../elements/Text';
import Icon from '../../../elements/Icon';

const ChecklistLoader = (props) => {
  const {
    labelKey = 'cmp-loading-fares',
    isLoading = true,
    spinnerContainer = {},
    spinnerItemContainer = {},
    textIdleSettings = {},
    textLoadingSettings = {},
    textCompletedSettings = {},
    iconCompletedSettings = {},
    iconActiveSettings = {},
    iconIdleSettings = {},
    enableAccessibilityMessage = false,
    stepDelay = 650,
    animationDuration = 650,
    checklistSteps = []
  } = props;
  const { translate, direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  const [completedSteps, setCompletedSteps] = React.useState(new Set());
  const [currentStep, setCurrentStep] = React.useState(null);

  React.useEffect(() => {
    if (!isLoading) {
      return;
    }
    const progressStep = (stepIndex) => {
      if (stepIndex >= checklistSteps.length) {
        return;
      }
      const timer = setTimeout(() => {
        setCompletedSteps((prev) => new Set([...prev, stepIndex]));
        setCurrentStep(stepIndex + 1);
        progressStep(stepIndex + 1);
      }, stepDelay + animationDuration);
      return timer;
    };
    const initialTimer = setTimeout(() => {
      setCurrentStep(0);
      progressStep(0);
    }, stepDelay);

    return () => {
      if (initialTimer) {
        clearTimeout(initialTimer);
      }
    };
  }, [isLoading, stepDelay, checklistSteps.length, animationDuration]);

  React.useEffect(() => {
    if (!isLoading) {
      setCompletedSteps(new Set());
      setCurrentStep(0);
    }
  }, [isLoading]);

  const StepIcon = ({ isCompleted, isActive }) => {
    const [animate, setAnimate] = React.useState(false);
    React.useEffect(() => {
      const frame = requestAnimationFrame(() => setAnimate(true));
      return () => cancelAnimationFrame(frame);
    }, [isActive]);

    if (isActive) {
      return (
        <>
          <Icon
            icon="check-circle"
            viewBox="0 0 24 24"
            size={24}
            omitBaseClasses={true}
            {...iconCompletedSettings}
            style={{
              opacity: isActive && animate ? 1 : 0,
              transition:
                isActive && animate ? `opacity ${animationDuration}ms ease-in-out` : 'none'
            }}
          />
          <Icon
            icon="checkbox-circle-outline"
            viewBox="0 0 24 24"
            size={24}
            omitBaseClasses={true}
            {...iconActiveSettings}
            style={{
              opacity: isActive && animate ? 0 : 1,
              transition: `opacity ${animationDuration}ms ease-in-out`
            }}
          />
        </>
      );
    } else if (isCompleted) {
      return (
        <>
          <Icon
            icon="check-circle"
            viewBox="0 0 24 24"
            size={24}
            omitBaseClasses={true}
            {...iconCompletedSettings}
            style={{ opacity: 1, transition: `opacity ${animationDuration}ms ease-in-out` }}
          />
          <Icon
            icon="checkbox-circle-outline"
            viewBox="0 0 24 24"
            size={24}
            omitBaseClasses={true}
            {...iconIdleSettings}
            style={{ opacity: 0, transition: `opacity ${animationDuration}ms ease-in-out` }}
          />
        </>
      );
    } else {
      return (
        <>
          <Icon
            icon="check-circle"
            viewBox="0 0 24 24"
            size={24}
            omitBaseClasses={true}
            {...iconCompletedSettings}
            style={{ opacity: 0, transition: `opacity ${animationDuration}ms ease-in-out` }}
          />
          <Icon
            icon="checkbox-circle-outline"
            viewBox="0 0 24 24"
            size={24}
            omitBaseClasses={true}
            {...iconIdleSettings}
            style={{ opacity: 1, transition: `opacity ${animationDuration}ms ease-in-out` }}
          />
        </>
      );
    }
  };

  return (
    <div
      className={`${className} checklist-loader`}
      style={style}
      role={enableAccessibilityMessage ? null : isLoading ? 'alert' : null}
      data-testid="checklist-loader"
      aria-label={isLoading ? translate(labelKey, 'Content is loading...') : null}
    >
      <Box flexDirection="column" className="checklist-container" {...spinnerContainer}>
        {checklistSteps.map((step, index) => {
          const isCompleted = completedSteps.has(index);
          const isActive = currentStep === index && !isCompleted;
          const textSettings = isCompleted
            ? textCompletedSettings
            : isActive
            ? textLoadingSettings
            : index === 0
            ? textLoadingSettings
            : textIdleSettings;

          return (
            <Box key={step} {...spinnerItemContainer}>
              <StepIcon isCompleted={isCompleted} isActive={isActive} />
              <Text {...textSettings}>{translate(step)}</Text>
            </Box>
          );
        })}
      </Box>
    </div>
  );
};

ChecklistLoader.propTypes = {
  /**
   * Show component icon
   */
  showIcon: PropTypes.bool,
  /**
   * label key props
   */
  labelKey: PropTypes.string,
  /**
   * It determines if the alert is
   * going to be assertive so that
   * for accessibility the SR reads
   * it, this prop is added because
   * in the map this alert is used
   * as a decorative element
   */
  isLoading: PropTypes.bool,
  /**
   * Delay between each step animation in milliseconds
   */
  stepDelay: PropTypes.number,
  /**
   * Duration of each animation in milliseconds
   */
  animationDuration: PropTypes.number,
  /**
   * Container styles for the spinner
   */
  spinnerContainer: PropTypes.object,
  /**
   * Text settings for the step labels
   */
  textSettings: PropTypes.object,
  /**
   * Enable accessibility messages
   */
  enableAccessibilityMessage: PropTypes.bool
};

ChecklistLoader.componentName = 'ChecklistLoader';

export default ChecklistLoader;
